import React, { useEffect } from "react";
import {
  Document,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { Colors } from "../../../assets/Colors";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { fetchPost } from "../../../fetchers";

// ... (Fungsi terbilang tetap sama)
const terbilang = (angka) => {
  // Handle angka 0
  if (angka === 0 || angka === 0.00) return "nol rupiah";

  const satuan = ["", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "delapan", "sembilan"];
  const belasan = ["", "sebelas", "dua belas", "tiga belas", "empat belas", "lima belas", "enam belas", "tujuh belas", "delapan belas", "sembilan belas"];
  const puluhan = ["", "sepuluh", "dua puluh", "tiga puluh", "empat puluh", "lima puluh", "enam puluh", "tujuh puluh", "delapan puluh", "sembilan puluh"];
  const ribuan = ["", "ribu", "juta", "miliar", "triliun"];

  // Fungsi untuk mengonversi 3 digit (ratusan)
  const convertRatusan = (num) => {
    if (num === 0) return "";
    
    let result = "";
    const ratus = Math.floor(num / 100);
    const sisa = num % 100;

    // Ratusan
    if (ratus > 0) {
      if (ratus === 1) {
        result = "seratus";
      } else {
        result = satuan[ratus] + " ratus";
      }
    }

    // Puluhan dan satuan
    if (sisa > 0) {
      if (result) result += " ";
      
      if (sisa < 10) {
        result += satuan[sisa];
      } else if (sisa < 20) {
        if (sisa === 10) {
          result += "sepuluh";
        } else {
          result += belasan[sisa - 10];
        }
      } else {
        const puluh = Math.floor(sisa / 10);
        const satu = sisa % 10;
        result += puluhan[puluh];
        if (satu > 0) {
          result += " " + satuan[satu];
        }
      }
    }

    return result;
  };

  // Pisahkan bagian integer dan desimal
  const integerPart = Math.floor(angka);
  let decimalPart = Math.round((angka - integerPart) * 100);
  
  // Handle floating point precision
  if (decimalPart === 100) {
    decimalPart = 0;
  }

  let number = integerPart;
  let result = "";

  if (number >= 1000000000000) {
    return "angka terlalu besar";
  }

  // Handle kasus khusus untuk 1000
  if (number === 1000) {
    result = "seribu";
  } else if (number === 0) {
    result = "nol";
  } else {
    // Proses per kelompok (ribuan, jutaan, dll)
    let groupIndex = 0;
    while (number > 0) {
      const group = number % 1000;
      
      if (group > 0) {
        let groupText = convertRatusan(group);
        
        // Tambahkan kata "ribu", "juta", dll
        if (groupIndex > 0) {
          // Handle kasus khusus untuk "seribu"
          if (groupIndex === 1 && group === 1) {
            groupText = "se" + ribuan[groupIndex];
          } else {
            groupText += " " + ribuan[groupIndex];
          }
        }
        
        // Gabungkan dengan hasil sebelumnya
        if (result) {
          result = groupText + " " + result;
        } else {
          result = groupText;
        }
      }
      
      number = Math.floor(number / 1000);
      groupIndex++;
    }
  }

  // Tambahkan "rupiah"
  result = result.trim() + " rupiah";

  // Tambahkan bagian desimal jika ada
  if (decimalPart > 0) {
    // Hapus "rupiah" sementara
    result = result.replace(" rupiah", "");
    
    let decimalText = "";
    if (decimalPart < 10) {
      decimalText = "nol " + satuan[decimalPart];
    } else if (decimalPart < 20) {
      if (decimalPart === 10) {
        decimalText = "sepuluh";
      } else {
        decimalText = belasan[decimalPart - 10];
      }
    } else {
      const puluh = Math.floor(decimalPart / 10);
      const satu = decimalPart % 10;
      decimalText = puluhan[puluh];
      if (satu > 0) {
        decimalText += " " + satuan[satu];
      }
    }
    
    result += " koma " + decimalText + " rupiah";
  }

  return result;
};

// Fungsi format tanggal
const formatDate = (val) => {
  if (!val) return "-";
  return new Date(val).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Fungsi untuk menampilkan informasi bank dari nomor rekening
const getBankInfo = (nomorRekening) => {
  if (!nomorRekening || nomorRekening === "-" || nomorRekening.trim() === "") {
    return {
      showBankInfo: false,
      bankText: "Informasi rekening akan diberikan oleh finance",
      accountName: "a.n. PT. Ranji Karya Sakti",
      note: "Hubungi finance untuk detail pembayaran"
    };
  }

  // Parse informasi dari string nomor rekening
  // Format yang diharapkan: "BANK MANDIRI a/c 1240 0002 025 99 IDR"
  let bankName = "";
  let accountNumber = "";
  
  // Cari nama bank (semua teks sebelum "a/c")
  const matchBank = nomorRekening.match(/^(.*?)\s*a\/c/i);
  if (matchBank && matchBank[1]) {
    bankName = matchBank[1].trim();
  } else {
    // Jika tidak ada "a/c", ambil semua teks sebelum angka pertama
    const matchBankAlt = nomorRekening.match(/^([^\d]+)/);
    if (matchBankAlt && matchBankAlt[1]) {
      bankName = matchBankAlt[1].trim();
    }
  }
  
  // Cari nomor rekening (angka setelah "a/c")
  const matchAccount = nomorRekening.match(/a\/c\s*([\d\s]+)/i);
  if (matchAccount && matchAccount[1]) {
    accountNumber = matchAccount[1].trim();
  } else {
    // Jika tidak ada "a/c", ambil semua angka dari string
    const numbers = nomorRekening.match(/\d+/g);
    if (numbers) {
      accountNumber = numbers.join(" ");
    }
  }
  
  // Jika bankName kosong, gunakan default
  if (!bankName) {
    bankName = nomorRekening;
  }

  return {
    showBankInfo: true,
    bankName: bankName,
    accountNumber: accountNumber,
    accountName: "PT. Ranji Karya Sakti"
  };
};

// Create styles with smaller fonts
const styles = StyleSheet.create({
  page: {
    paddingTop: 25,
    paddingBottom: 40,
    paddingHorizontal: 30,
    fontFamily: "Helvetica",
    fontSize: 7,
  },

  // System label
  systemLabel: {
    position: 'absolute',
    top: 5,
    left: 5,
    fontSize: 6,
    color: '#666',
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 2,
    borderWidth: 0.5,
    borderColor: '#ccc',
  },

  // Header styles
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
    borderBottomWidth: 0.5,
    borderBottomColor: "#333",
    paddingBottom: 10,
    marginTop: 8,
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 2,
  },
  companyAddress: {
    fontSize: 6,
    marginBottom: 1,
    color: "#555",
  },
  invoiceBox: {
    alignItems: "flex-end",
    flex: 1,
  },
  invoiceTitleContainer: {
    backgroundColor: "#f5f5f5", // Abu-abu terang
    paddingVertical: 4,
    paddingHorizontal: 15,
    marginBottom: 8,
    borderRadius: 3,
    borderWidth: 0.5,
    borderColor: "#ddd",
    alignSelf: "center",
  },
  invoiceTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#333",
    textAlign: "center",
  },

  invoiceInfo: {
    width: 180,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  infoLabel: {
    fontSize: 6,
    width: 60,
    fontWeight: "bold",
  },
  infoColon: {
    fontSize: 6,
    width: 8,
    textAlign: "center",
  },
  infoValue: {
    fontSize: 6,
    flex: 1,
  },

  // To section
  toSection: {
    marginBottom: 12,
    backgroundColor: "#f8f8f8",
    padding: 8,
    borderRadius: 3,
    borderWidth: 0.5,
    borderColor: "#ddd",
  },
  toTitle: {
    fontSize: 8,
    fontWeight: "bold",
    marginBottom: 3,
  },
  toText: {
    fontSize: 7,
    marginBottom: 1,
  },

  // Table styles
  table: {
    marginTop: 8,
    marginBottom: 5,
  },
  tableRowHeader: {
    flexDirection: "row",
    backgroundColor: "#444",
    color: "white",
    paddingVertical: 3,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ddd",
    paddingVertical: 4,
    minHeight: 18,
  },
  tableCell: {
    fontSize: 7,
    paddingHorizontal: 3,
    paddingVertical: 1,
  },
  tableCellNumber: {
    fontSize: 7,
    paddingHorizontal: 3,
    textAlign: "right",
    paddingVertical: 1,
  },
  // Judul halaman Lampiran
  lampiranTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 2,
  },
  // Baris judul kelompok Activity
  activityHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#eee",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ccc",
    paddingVertical: 3,
  },
  // Baris subtotal per Activity
  activitySubtotalRow: {
    flexDirection: "row",
    backgroundColor: "#f7f7f7",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ddd",
    paddingVertical: 3,
  },
  // Style baru untuk sel yang ditengah
  tableCellCenter: {
    fontSize: 7,
    paddingHorizontal: 3,
    paddingVertical: 1,
    textAlign: "center",
  },

  // Bottom section
  bottomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 15,
    fontSize: 7,
  },
  leftBox: {
    width: "65%",
  },
  rightBox: {
    width: "30%",
  },
  bold: {
    fontWeight: "bold",
  },
  italic: {
    fontStyle: "italic",
  },
  bankBox: {
    marginTop: 12,
    padding: 8,
    backgroundColor: "#f9f9f9",
    borderRadius: 3,
    borderWidth: 0.5,
    borderColor: "#eee",
  },
  bankTitle: {
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 6,
    textDecoration: "underline",
  },
  bankInfoRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  bankLabel: {
    fontSize: 6,
    width: 60,
    fontWeight: "bold",
  },
  bankColon: {
    fontSize: 6,
    width: 8,
    textAlign: "center",
  },
  bankValue: {
    fontSize: 6,
    flex: 1,
  },
  
  // Total table styles
  totalTable: {
    borderWidth: 0.5,
    borderColor: "#333",
    borderRadius: 3,
    overflow: "hidden",
  },
  totalTableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#eee",
  },
  totalTableCell: {
    fontSize: 7,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  totalTableLabel: {
    flex: 2,
    borderRightWidth: 0.5,
    borderRightColor: "#eee",
  },
  totalTableCurrency: {
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: "#eee",
    textAlign: "center",
    fontSize: 6,
    color: "#666",
    fontStyle: "italic",
  },
  totalTableValue: {
    flex: 2,
    textAlign: "right",
    paddingRight: 8,
  },
  totalTableHeader: {
    backgroundColor: "#f5f5f5",
    borderBottomWidth: 0.5,
    borderBottomColor: "#333",
  },
  totalTableHeaderCell: {
    fontSize: 7,
    fontWeight: "bold",
    paddingVertical: 3,
    paddingHorizontal: 6,
  },

  // Signature section
  signRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 40,
    paddingTop: 15,
    borderTopWidth: 0.5,
    borderTopColor: "#333",
    fontSize: 7,
  },
  signSpace: {
    marginTop: 25,
    marginBottom: 3,
  },

  // Additional compact styles
  compactText: {
    fontSize: 6,
  },
  sectionSpacing: {
    marginBottom: 8,
  },
  terbilangBox: {
    marginTop: 10,
    padding: 5,
    backgroundColor: "#f5f5f5",
    borderRadius: 3,
    borderWidth: 0.5,
    borderColor: "#000000",
  },
});

// Helper components with smaller fonts
const InfoRow = ({ label, value }) => (
  <View style={styles.infoRow}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoColon}>:</Text>
    <Text style={styles.infoValue}>{value || "-"}</Text>
  </View>
);

// Komponen BankInfo untuk menampilkan informasi bank dengan format baru
const BankInfo = ({ nomorRekening }) => {
  const bankInfo = getBankInfo(nomorRekening);
  
  if (!bankInfo.showBankInfo) {
    return (
      <View style={styles.bankBox}>
        <Text style={[styles.compactText, { marginBottom: 3 }]}>
          {bankInfo.bankText}
        </Text>
        <Text style={[styles.compactText, { marginBottom: 2 }]}>
          {bankInfo.accountName}
        </Text>
        <Text style={[styles.compactText, { fontStyle: "italic", color: "#666" }]}>
          {bankInfo.note}
        </Text>
      </View>
    );
  }
  
  return (
    <View style={styles.bankBox}>
      <Text style={styles.bankTitle}>Please Transfer To:</Text>
      
      <View style={styles.bankInfoRow}>
        <Text style={styles.bankLabel}>Name</Text>
        <Text style={styles.bankColon}>:</Text>
        <Text style={styles.bankValue}>{bankInfo.accountName}</Text>
      </View>
      
      <View style={styles.bankInfoRow}>
        <Text style={styles.bankLabel}>Bank</Text>
        <Text style={styles.bankColon}>:</Text>
        <Text style={styles.bankValue}>{bankInfo.bankName}</Text>
      </View>
      
      <View style={styles.bankInfoRow}>
        <Text style={styles.bankLabel}>Account No</Text>
        <Text style={styles.bankColon}>:</Text>
        <Text style={styles.bankValue}>{bankInfo.accountNumber}</Text>
      </View>
    </View>
  );
};

// TotalRow untuk tabel baru
const TotalTableRow = ({ label, value, bold = false, isLast = false }) => (
  <View style={[
    styles.totalTableRow,
    isLast && { 
      borderBottomWidth: 0,
      backgroundColor: bold ? "#f0f0f0" : "transparent" 
    }
  ]}>
    <View style={[styles.totalTableCell, styles.totalTableLabel]}>
      <Text style={bold && styles.bold}>{label}</Text>
    </View>
    <View style={[styles.totalTableCell, styles.totalTableCurrency]}>
      <Text style={bold && styles.bold}>IDR</Text>
    </View>
    <View style={[styles.totalTableCell, styles.totalTableValue]}>
      <Text style={bold && styles.bold}>
        {value !== undefined && value !== null
          ? value.toLocaleString("id-ID", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "0,00"}
      </Text>
    </View>
  </View>
);

const TableHeader = ({ columns }) => (
  <View style={styles.tableRowHeader}>
    {columns.map((col, index) => (
      <Text
        key={index}
        style={[
          col.key === "Qty" || col.key === "Satuan" ? styles.tableCellCenter : styles.tableCell,
          { width: `${col.width}%` },
          col.number && styles.tableCellNumber,
        ]}
      >
        {col.title}
      </Text>
    ))}
  </View>
);

const TableRow = ({ columns, data, index }) => (
  <View style={styles.tableRow}>
    {columns.map((col, colIndex) => {
      // Untuk nomor urut - kiri
      if (col.key === "no") {
        return (
          <Text
            key={colIndex}
            style={[styles.tableCell, { width: `${col.width}%` }]}
          >
            {index + 1}
          </Text>
        );
      }

      // Untuk deskripsi - kiri
      if (col.key === "desc") {
        return (
          <Text
            key={colIndex}
            style={[styles.tableCell, { width: `${col.width}%` }]}
          >
            {data[col.key] || ""}
          </Text>
        );
      }

      // Untuk Qty - tengah
      if (col.key === "Qty") {
        return (
          <Text
            key={colIndex}
            style={[styles.tableCellCenter, { width: `${col.width}%` }]}
          >
            {data[col.key] || "1"}
          </Text>
        );
      }
      
      // Untuk Unit - tengah
      if (col.key === "Satuan") {
        return (
          <Text
            key={colIndex}
            style={[styles.tableCellCenter, { width: `${col.width}%` }]}
          >
            {data[col.key] || "AU"}
          </Text>
        );
      }

      // Untuk jumlah dan harga (format angka) - kanan
      if (col.number) {
        const value = parseFloat(data[col.key] || 0);
        return (
          <Text
            key={colIndex}
            style={[styles.tableCellNumber, { width: `${col.width}%` }]}
          >
            {value.toLocaleString("id-ID", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        );
      }

      // Default untuk kolom lainnya
      return (
        <Text
          key={colIndex}
          style={[styles.tableCell, { width: `${col.width}%` }]}
        >
          {data[col.key] || "-"}
        </Text>
      );
    })}
  </View>
);

// Baris judul kelompok Activity (membentang penuh).
const ActivityHeaderRow = ({ name }) => (
  <View style={styles.activityHeaderRow}>
    <Text style={[styles.tableCell, styles.bold, { width: "100%" }]}>
      {name}
    </Text>
  </View>
);

// Baris subtotal per Activity.
const ActivitySubtotalRow = ({ columns, value }) => {
  const amountWidth = columns.find((c) => c.key === "Amount")?.width || 18;
  return (
    <View style={styles.activitySubtotalRow}>
      <Text style={[styles.tableCell, styles.bold, { width: `${100 - amountWidth}%`, textAlign: "right" }]}>
        Subtotal Activity
      </Text>
      <Text style={[styles.tableCellNumber, styles.bold, { width: `${amountWidth}%` }]}>
        {Number(value || 0).toLocaleString("id-ID", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </Text>
    </View>
  );
};

// Main Document Component
export const BlankoPOReturn = ({ data, NomorRekening }) => {
  const itemized = !!data?.Itemized;
  // Mode itemized (V2) menambah kolom "No. JR" sebelum Description.
  const tableColumns = itemized
    ? [
        { title: "No", key: "no", width: 4 },
        { title: "No. JR", key: "JrNo", width: 15 },
        { title: "Description", key: "desc", width: 35 },
        { title: "Qty", key: "Qty", width: 7 },
        { title: "Unit", key: "Satuan", width: 8 },
        { title: "Price", key: "UnitPrice", width: 13, number: true },
        { title: "Amounts", key: "Amount", width: 18, number: true },
      ]
    : [
        { title: "No", key: "no", width: 5 },
        { title: "Description", key: "desc", width: 50 },
        { title: "Qty", key: "Qty", width: 8 },
        { title: "Unit", key: "Satuan", width: 8 },
        { title: "Price", key: "UnitPrice", width: 13, number: true },
        { title: "Amounts", key: "Amount", width: 16, number: true },
      ];

  // Kolom untuk tabel utama mode itemized: ringkasan per Activity (No, Activity, Amount).
  const summaryColumns = [
    { title: "No", key: "no", width: 6 },
    { title: "Activity", key: "desc", width: 74 },
    { title: "Amounts", key: "Amount", width: 20, number: true },
  ];

  // MAPPING DATA DARI /getInvoiceNew
  const dpNumber = data?.DPNumber || data?.dp_number;
  const invoiceNo = data?.InvoiceNo || data?.invoice_no;
  const invoiceDate = data?.InvoiceDate || data?.invoice_date;
  const dueDate = data?.DueDate || data?.due_date;
  const contractName = data?.ContracNo || data?.contract_no || data?.Contract;
  const contractNumber = data?.NoKontrak || data?.no_kontrak;
  const quotationManual = data?.QuotationManual || data?.quatation_manual;
  const printCount = data?.PrintCount || data?.print_count;
  const revisi = data?.Revisi || data?.revisi;
  const quotationNo = data?.Contract || data?.quotation_no;
  const deskripsi = data?.Description || data?.description;
  const unit = data?.unit || data?.unit;
  const namaKlien = data?.NamaKlien || data?.klien_name;
  const alamat = data?.Alamat || data?.alamat;
  const cqName = data?.CQ; // invoice V2 per CQ; kosong = baris tak dicetak

  // Hitung total dari data detail (hanya untuk jumlah Rp)
  let subTotal = 0;
  if (data?.Detail && data.Detail.length > 0) {
    subTotal = data.Detail.reduce(
      (sum, item) => sum + parseFloat(item.Amount || 0),
      0,
    );
  }

  // Gunakan subtotal dari backend jika ada, jika tidak hitung dari detail
  const subtotalValue = data?.Subtotal || data?.subtotal || subTotal;
  // PPN mengikuti invoice (V2 mengirim TaxPct/TaxAmount/TotalAmount dari backend).
  // Data format lama tak membawa TaxPct → tetap 11% seperti sebelumnya.
  const hasTax = data?.TaxPct !== undefined && data?.TaxPct !== null && data?.TaxPct !== "";
  const taxPct = hasTax ? parseFloat(data.TaxPct) || 0 : 11;
  const vat = hasTax && data?.TaxAmount != null
    ? parseFloat(data.TaxAmount) || 0
    : subtotalValue * taxPct / 100;
  const grandTotal = hasTax && data?.TotalAmount != null
    ? parseFloat(data.TotalAmount) || 0
    : subtotalValue + vat;
  const vatLabel = `VAT ${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(taxPct)}%`;

  // Generate terbilang dari grandTotal
  const terbilangText = terbilang(grandTotal);

  // Buat data untuk 1 row summary
  const summaryItem = {
    desc: deskripsi || "Jasa/Pekerjaan",
    Satuan: unit ||"AU",
    Qty: "1", // Default 1
    UnitPrice: subtotalValue, // Biaya satuan sama dengan total (karena Qty = 1)
    Amount: subtotalValue, // Jumlah Rp adalah summary total
  };

  // Mode rinci per-item (dipakai invoice V2, lihat tableColumns di atas).
  // Memetakan tiap baris detail ke kolom tabel format lama.
  const detailRows = (data?.Detail || []).map((it) => ({
    ActivityName: it.ActivityName || it.Activity || "",
    LineGroup: it.LineGroup || "",
    JrNo: it.JrNo || it.JrID || "",
    desc: it.desc || it.ItemDisplay || it.ItemName || it.Description || "-",
    Qty: it.Qty ?? it.BilledQty ?? "",
    Satuan: it.Satuan || it.SatuanNama || it.SatuanID || "",
    UnitPrice: Number(it.UnitPrice ?? it.Rate ?? 0),
    Amount: Number(it.Amount ?? 0),
  }));

  // Label kelompok untuk item pendukung yang tak terikat Activity (ActivityName kosong),
  // dikelompokkan per LineGroup (Equipment/Labour/Truck/TWS).
  const LINEGROUP_LABELS = {
    EQUIPMENT: "Equipment",
    LABOUR: "Labour",
    TRUCK: "Truck",
    TWS: "TWS",
  };
  const groupLabelFor = (row) => {
    if (row.ActivityName) return row.ActivityName;
    if (row.LineGroup) return LINEGROUP_LABELS[row.LineGroup] || row.LineGroup;
    return "Lainnya";
  };

  // Kelompokkan baris detail per Activity; item tanpa Activity per LineGroup
  // (urutan kemunculan dipertahankan).
  const activityGroups = [];
  const activityIndex = {};
  detailRows.forEach((row) => {
    const key = groupLabelFor(row);
    if (activityIndex[key] === undefined) {
      activityIndex[key] = activityGroups.length;
      activityGroups.push({ name: key, rows: [], subtotal: 0 });
    }
    const g = activityGroups[activityIndex[key]];
    g.rows.push(row);
    g.subtotal += Number(row.Amount || 0);
  });

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* System Label */}
        <View style={styles.systemLabel}>
          <Text>System Generated</Text>
        </View>

        {/* ===== HEADER WITHOUT LOGO ===== */}
        <View style={styles.headerRow}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>TO</Text>
            <Text style={styles.companyName}>{namaKlien || "-"}</Text>
            <Text style={styles.companyAddress}>{alamat || "-"}</Text>
          </View>

          <View style={styles.invoiceBox}>
            {/* INVOICE Title dengan background abu-abu terang */}
            <View style={styles.invoiceTitleContainer}>
              <Text style={styles.invoiceTitle}>INVOICE</Text>
            </View>
            
            <View style={styles.invoiceInfo}>
              <InfoRow label="DP Number" value={dpNumber} />
              <InfoRow label="Invoice No" value={invoiceNo} />
              <InfoRow label="Inv. Date" value={formatDate(invoiceDate)} />
              <InfoRow label="Due Date" value={formatDate(dueDate)} />
              <InfoRow label="User" value={namaKlien} />
              {cqName ? <InfoRow label="CQ" value={cqName} /> : null}
              <InfoRow label="Contract" value={contractName} />
              <InfoRow label="Contract No" value={contractNumber} />
              <InfoRow label="Qoutation No" value={quotationManual} />
              <InfoRow label="Revisi" value={revisi} />
            </View>
          </View>
        </View>

        {/* ===== TO SECTION ===== */}
        <View style={styles.toSection}>
          <Text style={styles.toTitle}></Text>
          <Text style={styles.toText}></Text>
          <Text style={[styles.toText, styles.compactText]}></Text>
        </View>

        {/* ===== TABLE ===== */}
        <View style={styles.table}>
          <TableHeader columns={itemized ? summaryColumns : tableColumns} />

          {/* Itemized (V2): tabel utama hanya ringkasan per Activity (1 baris/Activity);
              rincian per item dipindah ke halaman Lampiran di bawah. */}
          {itemized && activityGroups.length > 0 ? (
            activityGroups.map((group, gi) => (
              <TableRow
                key={`sum-${gi}`}
                columns={summaryColumns}
                data={{ desc: group.name, Amount: group.subtotal }}
                index={gi}
              />
            ))
          ) : data?.Detail ? (
            <TableRow
              columns={tableColumns}
              data={summaryItem}
              index={0}
            />
          ) : (
            <View style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCell,
                  { width: "100%", textAlign: "center" },
                ]}
              >
                Tidak ada data
              </Text>
            </View>
          )}
        </View>

        {/* ===== TOTAL SECTION ===== */}
        <View style={styles.bottomRow}>
          <View style={styles.leftBox}>
            <View style={styles.terbilangBox}>
              <Text style={[styles.italic, { fontSize: 7, lineHeight: 1.2 }]}>
                {terbilangText}
              </Text>
            </View>

            {/* Bank Info dengan format baru */}
            <BankInfo nomorRekening={NomorRekening} />
          </View>

          <View style={styles.rightBox}>
            <View style={styles.totalTable}>
              <TotalTableRow label="Subtotal" value={subtotalValue} />
              <TotalTableRow label={vatLabel} value={vat} />
              <TotalTableRow label="TOTAL" value={grandTotal} bold isLast={true} />
            </View>
          </View>
        </View>

        {/* ===== FOOTER NOTES ===== */}
        <View style={[styles.sectionSpacing, { marginTop: 10 }]}>
          {/* Catatan bisa ditambahkan di sini */}
        </View>

        {/* ===== SIGNATURE SECTION - COMPACT ===== */}
        <View style={styles.signRow}>
          <View style={{ alignItems: "left", width: "45%" }}>
            <Text style={{ fontSize: 7, marginBottom: 15 }}>
              PT. Ranji Karya Sakti
            </Text>
            <Text style={styles.signSpace}></Text>
            <Text style={{ fontSize: 7, fontWeight: "bold" }}>
              ARIS SUGIYANTO
            </Text>
            <Text>_________________</Text>
            <Text style={{ fontSize: 6 }}>General Manager</Text>
          </View>

          <View style={{ alignItems: "left", width: "45%" }}>
            {/* <Text style={{ fontSize: 7, marginBottom: 15 }}>Menyetujui</Text>
            <Text style={styles.signSpace}>_________________</Text>
            <Text style={{ fontSize: 7 }}>(Client)</Text> */}
          </View>
        </View>

        {/* ===== FOOTER ===== */}
        <View style={{ position: "absolute", bottom: 20, left: 30, right: 30 }}>
          <Text style={{ fontSize: 6, textAlign: "center", color: "#666" }}>
            PT. RANJI KARYA SAKTI | Pendingin, Sanga Sanga, Kutai Kartanegara
            Regency, East Kalimantan 75267
          </Text>
          <Text
            style={{
              fontSize: 6,
              textAlign: "center",
              color: "#666",
              marginTop: 2,
            }}
          >
            Telp: (021) 22909819 | Email: info@ranjikaryasakti.co.id
          </Text>
        </View>
      </Page>

      {/* ===== HALAMAN LAMPIRAN: DETAIL PER ITEM (mode itemized/V2) ===== */}
      {itemized && detailRows.length > 0 && (
        <Page size="A4" style={styles.page} wrap>
          <View style={styles.systemLabel}>
            <Text>System Generated</Text>
          </View>

          <Text style={styles.lampiranTitle}>Lampiran — Detail per Item</Text>
          <Text style={{ fontSize: 7, marginBottom: 6, color: "#555" }}>
            Invoice No: {invoiceNo || "-"}
          </Text>

          <View style={styles.table}>
            <TableHeader columns={tableColumns} />
            {(() => {
              let rowNo = 0;
              return activityGroups.map((group, gi) => (
                <View key={`lamp-${gi}`} wrap={false}>
                  <ActivityHeaderRow name={group.name} />
                  {group.rows.map((row) => {
                    const idx = rowNo++;
                    return (
                      <TableRow key={idx} columns={tableColumns} data={row} index={idx} />
                    );
                  })}
                  <ActivitySubtotalRow columns={tableColumns} value={group.subtotal} />
                </View>
              ));
            })()}
          </View>

          {/* ===== FOOTER ===== */}
          <View style={{ position: "absolute", bottom: 20, left: 30, right: 30 }}>
            <Text style={{ fontSize: 6, textAlign: "center", color: "#666" }}>
              PT. RANJI KARYA SAKTI | Pendingin, Sanga Sanga, Kutai Kartanegara
              Regency, East Kalimantan 75267
            </Text>
            <Text style={{ fontSize: 6, textAlign: "center", color: "#666", marginTop: 2 }}>
              Telp: (021) 22909819 | Email: info@ranjikaryasakti.co.id
            </Text>
          </View>
        </Page>
      )}
    </Document>
  );
};

// Main Component
const BlankoPO = () => {
  const location = useLocation();

  // GANTI: menggunakan getInvoiceNew bukan getInvoice
  const mutationGetInvoice = useMutation((formData) =>
    fetchPost("getInvoiceNew", formData),
  );

  useEffect(() => {
    // Support untuk cara lama (Detail) dan cara baru (invoice_id)
    if (location.state?.invoice_id) {
      console.log(
        "📥 Mengambil data invoice dengan ID:",
        location.state.invoice_id,
      );
      mutationGetInvoice.mutate({
        invoice_id: location.state.invoice_id,
      });
    } else if (location.state?.Detail) {
      console.log("⚠️ Menggunakan cara lama");
      mutationGetInvoice.mutate({
        Detail: location.state.Detail,
        QuatationID: location.state.QuatationNo,
      });
    } else {
      console.error("❌ Tidak ada data invoice di state");
    }
  }, [location.state]);

  return (
    <div className="md:ml-64">
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4">
            {/* Header content here if needed */}
          </div>
        </div>
      </div>

      <div className="px-3 md:px-8 h-screen -mt-32">
        {mutationGetInvoice.data && (
          <div className="h-screen w-full overflow-hidden">
            <PDFViewer 
              className="h-screen w-full" 
              style={{ 
                width: '100%', 
                height: '100%',
                minWidth: '100%',
                minHeight: '100%'
              }}
            >
              <BlankoPOReturn
                data={mutationGetInvoice.data.data}
                NomorRekening={location.state?.NomorRekening}
              />
            </PDFViewer>
          </div>
        )}

        {mutationGetInvoice.isLoading && (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading document...</p>
            </div>
          </div>
        )}

        {mutationGetInvoice.error && (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <div className="text-red-500 text-4xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold text-red-700">
                Error Loading Invoice
              </h3>
              <p className="text-gray-600 mt-2">
                {mutationGetInvoice.error.message || "Failed to load invoice"}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlankoPO;